import { useState, useMemo } from 'react';
import { X, ShoppingCart, Check, /* Star, */ Package, ExternalLink, Minus, Plus } from 'lucide-react';
import { Product } from '@/lib/Apis';
import { useCart } from '@/providers/CartProvider';
import Image from 'next/image';
import { ProductVariantInventoryStatusEnum } from '@/lib/Apis';
import { useNotification } from '@/providers/NotificationProvider';
import Link from 'next/link';
import { PLACEHOLDER_IMAGE_SRC } from '@/lib/constants/assets';
import { NotifyWhenInStockButton } from '@/components/NotifyWhenInStockButton';

// Find the best default variant index: lowest-priced in-stock, or lowest-priced overall
function getBestDefaultVariantIndex(variants: Product['variants']): number {
  if (!variants || variants.length === 0) return 0;

  const inStockIndices = variants
    .map((v, i) => ({ v, i }))
    .filter(({ v }) => (v.inventoryCount ?? 0) > 0);

  const pool = inStockIndices.length > 0
    ? inStockIndices
    : variants.map((v, i) => ({ v, i }));

  return pool.reduce((best, current) =>
    (current.v.finalPrice ?? Infinity) < (best.v.finalPrice ?? Infinity) ? current : best
    , pool[0]).i;
}

interface QuickViewModalProps {
  product: Product;
  onClose: () => void;
  onNavigateToProduct?: (productId: string) => void;
}

export function QuickViewModal({ product, onClose, onNavigateToProduct }: QuickViewModalProps) {
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(() => getBestDefaultVariantIndex(product.variants));
  const [selectedSizeIndex, setSelectedSizeIndex] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [addedToCart, setAddedToCart] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const notification = useNotification();

  const selectedVariant = product.variants?.[selectedVariantIndex] || product.variants?.[0];
  const selectedSize = selectedVariant?.media?.[selectedSizeIndex];
  const displayPrice = selectedVariant?.finalPrice || (product as any).finalPrice || (product as any).price || 0;
  const displayOriginalPrice = selectedVariant?.retailPrice || (product as any).retailPrice || (product as any).compareAtPrice || 0;
  const isDiscounted = selectedVariant?.isDiscounted ?? (product as any).isDiscounted;
  const discountAmount = selectedVariant?.discountAmount ?? (product as any).discountAmount;
  const displayName = product.name;

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= (selectedVariant?.inventoryCount || 10)) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = async () => {
    if (!product || !selectedVariant) return;

    setIsAddingToCart(true);
    try {
      await addToCart(
        product.id,
        quantity,
        selectedVariant._id
      );
    } catch (error) {
      console.error('Failed to add to cart', error);
    } finally {
      setIsAddingToCart(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[32px] max-w-5xl w-full max-h-[90vh] overflow-hidden relative flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-2 hover:bg-gray-100 rounded-full transition-colors z-20 bg-white/80 backdrop-blur-sm shadow-sm"
        >
          <X className="size-6 text-hmuted" />
        </button>

        <div className="overflow-y-auto p-8 flex-1 custom-scrollbar">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Product Image */}
            <div className="space-y-4">
              <div className="relative aspect-3/4 rounded-[24px] overflow-hidden bg-gray-50">
                <Image
                  src={selectedVariant?.media?.[selectedImageIndex]?.file || selectedVariant?.media?.[0]?.file || product.media?.[0]?.file || PLACEHOLDER_IMAGE_SRC}
                  alt={product.name}
                  className="w-full h-full object-contain"
                  width={500}
                  height={500}
                />
                {/* Badges */}
                <div className="absolute top-4 left-4 flex flex-col gap-2">
                  {isDiscounted && (
                    <div className="bg-haccent text-white rounded-full px-3 py-1.5">
                      <span className="font-bold text-[11px] uppercase">
                        -{discountAmount}%
                      </span>
                    </div>
                  )}
                  {/* {product.bestseller && (
                    <div className="bg-hsecondary text-white rounded-full px-3 py-1.5">
                      <span className="font-semibold text-[10px] uppercase">
                        Bestseller
                      </span>
                    </div>
                  )} */}
                </div>
              </div>

              {/* Thumbnail Images */}
              {selectedVariant?.media && selectedVariant.media.length > 1 && (
                <div className="grid grid-cols-4 gap-3">
                  {selectedVariant.media.map((image: any, index: any) => (
                    <div
                      key={index}
                      className={`aspect-square rounded-xl overflow-hidden cursor-pointer transition-opacity ${selectedImageIndex === index ? 'ring-2 ring-hprimary' : 'bg-gray-50 hover:opacity-75'}`}
                      onClick={() => setSelectedImageIndex(index)}
                    >
                      <img
                        src={image.file}
                        alt={`${product.name} ${index + 1}`}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Product Details */}
            <div className="flex flex-col">
              <div className="mb-6">
                <p className="text-[11px] text-hprimary uppercase tracking-wide font-medium mb-2">
                  {product.brand}
                </p>
                <h2 className="text-3xl font-bold text-hsecondary tracking-tight">
                  {displayName}
                </h2>
                {selectedVariant?.name && selectedVariant.name !== product.name && (
                  <p className="text-sm text-hmuted mt-1">
                    {selectedVariant.name}
                  </p>
                )}

                {/* Reviews are temporarily hidden.
                <div className="flex items-center gap-2 mt-3">
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`size-4 ${i < Math.floor(product.summary?.averageRating || 0)
                          ? 'text-haccent fill-accent'
                          : 'text-gray-300'
                          }`}
                      />
                    ))}
                  </div>
                  <span className="text-[13px] text-hmuted">
                    {product.summary?.averageRating || 0} ({product.summary?.reviewCount || 0} reviews)
                  </span>
                </div>
                */}
              </div>

              {/* Price */}
              <div className="flex items-center gap-3 mb-4">
                <span className="font-bold text-[32px] text-haccent">
                  ${displayPrice.toFixed(2)}
                </span>
                {isDiscounted && (
                  <span className="text-[20px] text-hmuted line-through">
                    ${displayOriginalPrice.toFixed(2)}
                  </span>
                )}
              </div>

              {/* Stock Status */}
              <div className="flex flex-col gap-1 mb-6">
                <div className="flex items-center gap-2">
                  {selectedVariant?.inventoryCount === 0 ? (
                    <span className="text-[12px] text-red-500 font-medium">
                      Out of Stock
                    </span>
                  ) : selectedVariant?.inventoryCount && selectedVariant.inventoryCount <= 10 ? (
                    <span className="text-[12px] text-hprimary font-medium flex items-center gap-1">
                      <Package className="size-3" />
                      Only {selectedVariant.inventoryCount} left in stock
                    </span>
                  ) : (
                    <span className="text-[12px] text-green-600 font-medium flex items-center gap-1">
                      <Package className="size-3" />
                      In Stock
                    </span>
                  )}
                </div>
                {(selectedVariant?.sku || product.sku) && (
                  <p className="text-[11px] text-hmuted">
                    SKU: {selectedVariant?.sku || product.sku}
                  </p>
                )}
              </div>

              {/* Physical Details */}
              {Boolean(selectedVariant?.weight || selectedVariant?.width || selectedVariant?.height || selectedVariant?.length) && (
                <div className="grid grid-cols-2 gap-4 mb-6 p-3 bg-gray-50/50 rounded-xl border border-gray-100">
                  {selectedVariant?.weight > 0 && (
                    <div>
                      <p className="text-[10px] text-hmuted uppercase font-semibold">Weight</p>
                      <p className="text-[12px] text-hsecondary">{selectedVariant.weight}g</p>
                    </div>
                  )}
                  {(selectedVariant?.width > 0 || selectedVariant?.height > 0 || selectedVariant?.length > 0) && (
                    <div>
                      <p className="text-[10px] text-hmuted uppercase font-semibold">Dimensions</p>
                      <p className="text-[12px] text-hsecondary">
                        {[selectedVariant?.length, selectedVariant?.width, selectedVariant?.height]
                          .filter(v => (v || 0) > 0)
                          .join(' × ')} cm
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Description */}
              <div
                className="text-[14px] text-hmuted leading-[1.7] mb-6 max-w-full overflow-hidden break-words"
                dangerouslySetInnerHTML={{ __html: selectedVariant?.description || product.description }}
              />

              {/* Color Selection */}
              {product.variants.length > 1 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-[13px] text-hsecondary mb-3">
                    Selected Variant: <span className="font-normal text-hmuted">{product.variants[selectedVariantIndex].name}</span>
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {product.variants.map((variant: any, index: any) => (
                      <button
                        key={variant.id}
                        onClick={() => {
                          setSelectedVariantIndex(index);
                          setSelectedSizeIndex(0);
                          setSelectedImageIndex(0); // Reset selected image index when variant changes
                        }}
                        className={`size-10 rounded-full border-2 transition-all ${selectedVariantIndex === index
                          ? 'border-hprimary scale-110'
                          : 'border-gray-200 hover:border-hprimary-50'
                          }`}
                        style={{ backgroundColor: variant.colorHex }}
                        title={variant.color}
                      >
                        <Image
                          src={variant.media?.[0]?.file || product.media?.[0]?.file || PLACEHOLDER_IMAGE_SRC}
                          alt={variant.color || `Variant ${index + 1}`}
                          className="object-cover"
                          height={32}
                          width={32}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Quick Features */}
              {product?.tags?.length > 0 &&
                <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                  <ul className="space-y-2">
                    {product?.tags?.slice(0, 3).map((feature: any, index: any) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="size-4 text-hprimary shrink-0 mt-0.5" />
                        <span className="text-[12px] text-hmuted">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>}

              {/* Quantity Selector */}
              <div className="mb-6">
                <h3 className="font-semibold text-[13px] text-hsecondary mb-3">
                  Quantity
                </h3>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handleQuantityChange(quantity - 1)}
                      disabled={quantity <= 1}
                      className="p-2 rounded-full border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Minus className="size-4 text-hsecondary" />
                    </button>
                    <span className="w-8 text-center font-medium">{quantity}</span>
                    <button
                      onClick={() => {
                        const maxStock = selectedVariant?.inventoryCount || 999;
                        if (quantity >= maxStock) {
                          notification.error(
                            'Maximum Stock Reached',
                            `Sorry, only ${maxStock} units of this item are available.`
                          );
                          return;
                        }
                        handleQuantityChange(quantity + 1);
                      }}
                      className="p-2 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <Plus className="size-4 text-hsecondary" />
                    </button>
                  </div>
                  {quantity >= (selectedVariant?.inventoryCount || 999) && (
                    <p className="text-[11px] text-hprimary font-medium px-2">
                      Maximum available reached
                    </p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 mt-auto">
                {selectedVariant?.inventoryCount === 0 || selectedVariant?.inventoryStatus === ProductVariantInventoryStatusEnum.OUTOFSTOCK ? (
                  <NotifyWhenInStockButton
                    productId={product._id || product.id}
                    variantId={selectedVariant._id || selectedVariant.id}
                  />
                ) : (
                  <button
                    onClick={handleAddToCart}
                    disabled={addedToCart}
                    className={`w-full font-medium text-[14px] px-6 py-4 rounded-full transition-all duration-300 flex items-center justify-center gap-3 ${addedToCart
                      ? 'bg-green-500 text-white'
                      : 'bg-hprimary text-white hover:bg-[#d66f45] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed'
                      }`}
                  >
                    {isAddingToCart ? (
                      <>
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="h-4 w-4" />
                        {!selectedVariant ? 'Select a variant' : 'Add to Cart'}
                      </>
                    )}
                  </button>
                )}
                <Link href={`/products/${product._id}`}>
                  <button
                    onClick={() => {
                      onClose();
                      // onNavigateToProduct?.(product._id || product.id);
                    }}
                    className="w-full font-medium text-[13px] px-6 py-3 rounded-full bg-white text-hsecondary border-2 border-hprimary hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                  >
                    View Full Details
                    <ExternalLink className="size-4" />
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
